import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, Form, Input, Button, Alert, Typography, Select } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { useAuth } from '../auth/AuthProvider';
import { ApiError } from '../api/types';
import { SavedSessions } from '../auth/SavedSessions';
import { SavedSession, LOGIN_EMAIL_STORAGE_KEY, normalizeApiUrl } from '../auth/session';
import {
  ApiEnvironment,
  readApiEnvironment,
  readCustomApiUrl,
} from '../config/env';

const { Title, Text } = Typography;

interface LoginValues {
  email: string;
  password: string;
  environment: ApiEnvironment;
  customUrl?: string;
}

export const LoginPage: React.FC<{ embedded?: boolean; initialSession?: SavedSession; onSuccess?(): void; onLoading?(loading: boolean): void }> = ({ embedded = false, initialSession, onSuccess, onLoading }) => {
  const { isAuthenticated, isRestoring, login, authNotice, clearAuthNotice } =
    useAuth();
  const [form] = Form.useForm();
  const [reauthenticating, setReauthenticating] = useState(initialSession);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [environment, setEnvironment] = useState<ApiEnvironment>(
    () => initialSession?.environment || readApiEnvironment()
  );

  if (!embedded && !isRestoring && isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  const handleSubmit = async (values: LoginValues) => {
    clearAuthNotice();
    setFormError(null);
    setLoading(true);
    onLoading?.(true);
    try {
      await login(values.email, values.password, { environment: values.environment, customUrl: values.customUrl, sessionId: reauthenticating?.id });
      onSuccess?.();
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
      } else if (err instanceof Error) {
        setFormError(err.message);
      } else {
        setFormError('Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
      onLoading?.(false);
    }
  };

  return (
    <Card
      style={{ maxWidth: 420, width: '100%', marginInline: 'auto' }}
      className="shadow-md rounded-lg"
    >
      <div className="text-center mb-6">
        <Title level={3} style={{ marginBottom: 4 }}>
          Golden Hounds Admin
        </Title>
        <Text type="secondary">Sign in to manage geo access</Text>
      </div>

      {authNotice && (
        <Alert
          message={authNotice}
          type="warning"
          showIcon
          className="mb-4"
          closable
          onClose={clearAuthNotice}
        />
      )}

      {formError && (
        <Alert
          message={formError}
          type="error"
          showIcon
          className="mb-4"
          closable
          onClose={() => setFormError(null)}
        />
      )}

      {!embedded && <div className="mb-4"><SavedSessions onReauthenticate={session => {
        setReauthenticating(session);
        setEnvironment(session.environment);
        form.setFieldsValue({ email: session.email === 'Existing session' ? '' : session.email, environment: session.environment, customUrl: session.apiUrl, password: '' });
      }} /></div>}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        initialValues={{
          email: initialSession?.email === 'Existing session' ? '' : initialSession?.email || window.localStorage.getItem(LOGIN_EMAIL_STORAGE_KEY) || '',
          environment,
          customUrl: initialSession?.apiUrl || readCustomApiUrl(),
        }}
        onValuesChange={(changedValues: Partial<LoginValues>) => {
          if (changedValues.environment) {
            setEnvironment(changedValues.environment);
          }
          if (authNotice) clearAuthNotice();
          if (formError) setFormError(null);
        }}
      >
        <Form.Item name="environment" label="Environment">
          <Select
            size="large"
            options={[
              { value: 'dev', label: 'Dev' },
              { value: 'staging', label: 'Staging' },
              { value: 'staging-testpay', label: 'Staging Test Paysegment' },
              { value: 'production', label: 'Production' },
              { value: 'local', label: 'Local' },
              { value: 'other', label: 'Other' },
            ]}
          />
        </Form.Item>

        {environment === 'other' && (
          <Form.Item
            name="customUrl"
            label="API URL"
            validateTrigger="onBlur"
            rules={[
              { required: true, message: 'Please enter the API URL' },
              {
                validator: (_, value) => {
                  try {
                    normalizeApiUrl(value);
                    return Promise.resolve();
                  } catch {
                    return Promise.reject(new Error('Please enter a valid URL'));
                  }
                },
              },
            ]}
          >
            <Input placeholder="https://api.example.com" size="large" />
          </Form.Item>
        )}

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email address' },
          ]}
        >
          <Input
            prefix={<MailOutlined className="text-gray-400" />}
            placeholder="admin@example.com"
            size="large"
            autoComplete="email"
          />
        </Form.Item>

        <Form.Item
          name="password"
          label="Password"
          rules={[{ required: true, message: 'Please enter your password' }]}
        >
          <Input.Password
            prefix={<LockOutlined className="text-gray-400" />}
            placeholder="Password"
            size="large"
            autoComplete="current-password"
          />
        </Form.Item>

        <Form.Item className="mt-6 mb-0">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={loading}
          >
            Sign in
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};
